import { Set as ISet, List, Record } from '@gitbook/util-immutable';

import { isType, MODEL_TYPES } from '../constants';
import { Mark } from './mark';
import { TextRange } from './text-range';

interface LeafProperties {
    marks: ISet<Mark>;
    selections: ISet<TextRange>;
    text: string;
}

export type LeafCreateAttrs =
    | string
    | Partial<Omit<LeafProperties, 'marks'> & { marks: Mark[] | ISet<Mark> }>
    | Partial<Omit<LeafProperties, 'selections'> & { selections: TextRange[] | ISet<TextRange> }>;

/*
 * Leaf of text annotated with marks.
 */
export class Leaf extends Record<LeafProperties>({
    marks: ISet.create(),
    selections: ISet.create(),
    text: '',
}) {
    public declare readonly text: string;
    public declare readonly marks: ISet<Mark>;

    /**
     * Selections displayed on the leaf.
     * This property is used only during rendering and is not persisted in the document.
     */
    public declare readonly selections: ISet<TextRange>;

    /*
     * Object.
     */
    get object(): 'leaf' {
        return 'leaf';
    }

    /*
     * Check if `input` is a `Leaf`.
     */
    public static isLeaf(input: any): input is Leaf {
        return isType('LEAF', input);
    }

    /*
     * Create a new `Leaf` with `attrs`.
     */
    public static create(attrs: LeafCreateAttrs = {}): Leaf {
        if (typeof attrs === 'string') {
            return Leaf.createRecord({
                text: attrs,
            });
        }

        return Leaf.createRecord({
            text: attrs.text || '',
            marks: Mark.createSet(attrs.marks || []),
            selections: TextRange.createSet(attrs.selections || []),
        });
    }

    /*
     * Create a valid List of `Leaf` from `leaves`.
     */
    public static createLeaves(leaves: List<Leaf>): List<Leaf> {
        if (leaves.size <= 1) {
            return leaves;
        }

        let invalid = false;

        // TODO: we can make this faster with [List] and then flatten
        const cache: Leaf[] = [];
        // Search from the leaves left end to find invalid node;
        leaves.findLast((leaf) => {
            const firstLeaf = cache[0];

            // If the first leaf of cache exist, check whether the first leaf is connectable with the current leaf
            if (firstLeaf) {
                // If marks and selections equals, then the two leaves can be connected
                if (
                    firstLeaf.marks.equals(leaf.marks) &&
                    firstLeaf.selections.equals(leaf.selections)
                ) {
                    invalid = true;
                    cache[0] = firstLeaf.set('text', `${leaf.text}${firstLeaf.text}`);
                    return false;
                }

                // If the cached leaf is empty, drop the empty leaf with the upcoming leaf
                if (firstLeaf.text === '') {
                    invalid = true;
                    cache[0] = leaf;
                    return false;
                }

                // If the current leaf is empty, drop the leaf
                if (leaf.text === '') {
                    invalid = true;
                    return false;
                }
            }

            cache.unshift(leaf);
            return false;
        });

        const result = List.create<Leaf>(cache);

        if (!invalid) {
            return leaves;
        }
        return result;
    }

    /*
     * Split a list of leaves to two lists; if the leaves are valid leaves, the returned leaves are also valid
     * Corner Cases:
     *   1. if offset is smaller than 0, then return [List(), leaves]
     *   2. if offset is bigger than the text length, then return [leaves, List.create()]
     */
    public static splitLeaves(leaves: List<Leaf>, offset: number): Array<List<Leaf>> {
        if (offset < 0) {
            return [List.create(), leaves];
        }

        if (leaves.size === 0) {
            return [List.create(), List.create()];
        }

        let endOffset = 0;
        let index = -1;
        let left;
        let right;

        leaves.find((leaf) => {
            index++;
            const startOffset = endOffset;
            const { text } = leaf;
            endOffset += text ? text.length : 0;

            if (endOffset < offset) {
                return false;
            }
            if (startOffset > offset) {
                return false;
            }

            const length = offset - startOffset;
            left = leaf.set('text', text.slice(0, length));
            right = leaf.set('text', text.slice(length));
            return true;
        });

        if (!left) {
            return [leaves, List.create()];
        }

        if (left.text === '') {
            if (index === 0) {
                return [List.create([left]), leaves];
            }

            return [leaves.take(index), leaves.skip(index)];
        }

        if (right.text === '') {
            if (index === leaves.size - 1) {
                return [leaves, List.create([right])];
            }

            return [leaves.take(index + 1), leaves.skip(index + 1)];
        }

        return [leaves.take(index).push(left), leaves.skip(index + 1).unshift(right)];
    }

    /*
     * Check if `input` is a list of leaves.
     */
    public static isLeafList(input: any): input is List<Leaf> {
        return List.isList(input) && input.every((item) => Leaf.isLeaf(item));
    }

    /*
     * Update a `mark` at leaf, replace with newMark
     */
    public updateMark(mark: Mark, newMark: Mark): Leaf {
        const { marks } = this;
        if (newMark.equals(mark)) {
            return this;
        }

        const existingMark = marks.deref(mark);
        if (!existingMark) {
            return this;
        }
        const newMarks = marks.withMutations((collection) => {
            collection.delete(existingMark);
            collection.add(newMark);
        });
        return this.set('marks', newMarks);
    }

    /*
     * Add a `set` of marks at `index` and `length`.
     */
    public addMarks(set: ISet<Mark>): Leaf {
        const { marks } = this;
        return this.set('marks', marks.union(set));
    }

    /*
     * Remove a `mark` at `index` and `length`.
     */
    public removeMark(mark: Mark): Leaf {
        const { marks } = this;
        return this.set('marks', marks.delete(mark));
    }

    /*
     * Add a `range` selection at `index` and `length`.
     */
    public addSelection(selection: TextRange): Leaf {
        const { selections } = this;
        return this.set('selections', selections.add(selection));
    }
}

/*
 * Attach a pseudo-symbol for type checking.
 */

Leaf.prototype[MODEL_TYPES.LEAF] = true;
